import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../../components/ui/EmptyState";
import { haversineMeters } from "../../lib/geo";
import type { LatLng } from "../map/geohash";

import type { StationListItem } from "./api";
import { StationCard } from "./StationCard";

export type SortMode = "nearest" | "fast";

type Props = {
  stations: StationListItem[];
  sort: SortMode;
  origin: LatLng;
};

export function StationList({ stations, sort, origin }: Props) {
  const { t } = useTranslation();

  const withDistance = useMemo(
    () =>
      stations.map((station) => ({
        station,
        distanceM: haversineMeters(origin.lat, origin.lng, station.lat, station.lng),
      })),
    [stations, origin.lat, origin.lng],
  );

  const sorted = useMemo(() => {
    const items = [...withDistance];
    if (sort === "nearest") {
      items.sort((a, b) => a.distanceM - b.distanceM);
    } else {
      items.sort((a, b) => compareFastFirst(a.station, b.station));
    }
    return items;
  }, [withDistance, sort]);

  if (sorted.length === 0) {
    return (
      <div className="p-3 sm:p-4">
        <EmptyState
          glyph="📍"
          eyebrow={t("stations.list_empty_eyebrow")}
          title={t("stations.list_empty_title")}
          body={t("stations.list_empty")}
        />
      </div>
    );
  }

  return (
    <ul className="space-y-3 p-3 sm:p-4">
      {sorted.map(({ station, distanceM }) => (
        <li key={station.id}>
          <StationCard station={station} distanceM={distanceM} />
        </li>
      ))}
    </ul>
  );
}

// Fast-charging sort key: more fast chargers first, then more available, then
// more total, then by name for stable ordering.
function compareFastFirst(a: StationListItem, b: StationListItem): number {
  if (b.fastChargerCount !== a.fastChargerCount) {
    return b.fastChargerCount - a.fastChargerCount;
  }
  if (b.availableCount !== a.availableCount) {
    return b.availableCount - a.availableCount;
  }
  if (b.chargerCount !== a.chargerCount) {
    return b.chargerCount - a.chargerCount;
  }
  return a.name.localeCompare(b.name);
}
